import { lazy, Suspense } from 'react';
import { Switch, Redirect } from 'react-router-dom';
import LoadingScreen from './common/LoadingScreen';
import ScrollToTop from './components/ScrollToTop';
import { RouteWithLayout } from './components';
import { Main as MainLayout, Minimal as MinimalLayout } from './layouts';
import { PUBLIC_ROUTES, PROTECTED_ROUTES } from './config/navigation';

const COMPONENT_REGISTRY = {
  Home: lazy(() => import('./views/Home/index')),
  SignIn: lazy(() => import('./views/SignIn/index')),
  SignOut: lazy(() => import('./views/SignOut/index')),
  NotFound: lazy(() => import('./views/NotFound/index')),
  SsoError: lazy(() => import('./views/SsoError/index')),
  NotSupportRole: lazy(() => import('./views/NotSupportRole/index')),
  ForgotPassword: lazy(() => import('./views/ForgotPassword/index')),
  Register: lazy(() => import('./views/Register/index')),
  RegisterOTP: lazy(() => import('./views/RegisterOTP/index')),
  RegisterPassword: lazy(() => import('./views/RegisterPassword/index')),
  RegisterInfoPhone: lazy(() => import('./views/RegisterInfoPhone/index')),
  ChangePasswordOTP: lazy(() => import('./views/ChangePasswordOTP/index')),
  ChangePasswordUser: lazy(() => import('./views/ChangePasswordUser/index')),
  UpdateUserInfo: lazy(() => import('./views/UpdateUserInfo/index')),

  StudentIndex: lazy(() => import('./views/StudentIndex/index')),
  StudentHistory: lazy(() => import('./views/StudentHistory/index')),
  StudentExamModule: lazy(() => import('./views/StudentExamModule')),
  StudentViewExam: lazy(() => import('./views/StudentViewExam/index')),
  StudentViewExamTHPT2025: lazy(
    () => import('./views/StudentViewExamTHPT2025/index')
  ),
  StudentDocument: lazy(() => import('./views/StudentDocument/index')),

  TeacherKhoi: lazy(() => import('./views/TeacherKhoi/index')),
  TeacherKhoiTaoPhongThi: lazy(
    () => import('./views/TeacherKhoiTaoPhongThi/index')
  ),
  TeacherLop: lazy(() => import('./views/TeacherLop/index')),
  TeacherLopChiTiet: lazy(() => import('./views/TeacherLopChiTiet/index')),
  TeacherStepThemHocSinh: lazy(
    () => import('./views/TeacherStepThemHocSinh/index')
  ),
  TeacherStepThemHocSinhPhong: lazy(
    () => import('./views/TeacherStepThemHocSinhPhong/index')
  ),
  TeacherStepChuyenDe: lazy(() => import('./views/TeacherStepChuyenDe/index')),
  TeacherListTeacher: lazy(() => import('./views/TeacherListTeacher/index')),
  TeacherManageClass: lazy(() => import('./views/TeacherManageClass/index')),

  TeacherTryExam: lazy(() => import('./views/TeacherTryExam/index')),
  TeacherTryExamTHPTQG: lazy(
    () => import('./views/TeacherTryExamTHPTQG/index')
  ),
  TeacherXuLyTinhHuongThi: lazy(
    () => import('./views/TeacherXuLyTinhHuongThi/index')
  ),

  TeacherQLKyThi: lazy(() => import('./views/TeacherQLKyThi/index')),
  TeacherQLMon: lazy(() => import('./views/TeacherQLMon/index')),

  PointManagement: lazy(() => import('./views/PointManagement/index')),
  TeacherDiemDanhSach: lazy(() => import('./views/TeacherDiemDanhSach/index')),
  TeacherDiemChiTiet: lazy(() => import('./views/TeacherDiemChiTiet/index')),

  TeacherDeThiCanLam: lazy(() => import('./views/TeacherDeThiCanLam/index')),
  TeacherDeThi: lazy(() => import('./views/TeacherDeThi/index')),
  TeacherDeThiDanhSachCauHoi: lazy(
    () => import('./views/TeacherDeThiDanhSachCauHoi/index')
  ),
  TeacherDeThiDanhSachCauHoiCapNhat: lazy(
    () => import('./views/TeacherDeThiDanhSachCauHoiCapNhat/index')
  ),
  TeacherDeThiDanhSachCauHoiCapNhatTHPT2025: lazy(
    () => import('./views/TeacherDeThiDanhSachCauHoiCapNhatTHPT2025/index')
  ),
  TeacherStepUploadDe: lazy(() => import('./views/TeacherStepUploadDe/index')),
  TeacherStepUploadDeTHPTQG: lazy(
    () => import('./views/TeacherStepUploadDeTHPTQG/index')
  ),
  TeacherTaoDeThiTuNganHang: lazy(
    () => import('./views/TeacherTaoDeThiTuNganHang/index')
  ),
  TeacherListTransfer: lazy(() => import('./views/TeacherListTransfer/index')),

  TeacherViewExam: lazy(() => import('./views/TeacherViewExam/index')),
  TeacherViewExamTHPTQG: lazy(() => import('views/TeacherViewExamTHPTQG')),
  TeacherHanhTrinhLamBai: lazy(
    () => import('./views/TeacherHanhTrinhLamBai/index')
  ),
  TeacherPrintExam: lazy(() => import('./views/TeacherPrintExam/index')),

  TeacherIndexEssay: lazy(() => import('./views/TeacherIndexEssay/index')),
  TeacherChuaChamDiem: lazy(() => import('./views/TeacherChuaChamDiem/index')),
  TeacherDaChamDiem: lazy(() => import('./views/TeacherDaChamDiem/index')),
  TeacherDeThiTuLuan: lazy(() => import('./views/TeacherDeThiTuLuan/index')),
  TeacherScoreExamEssay: lazy(
    () => import('./views/TeacherScoreExamEssay/index')
  ),

  TeacherDSMauPhieuCham: lazy(
    () => import('./views/TeacherDSMauPhieuCham/index')
  ),
  TeacherMauPhieuCham: lazy(() => import('./views/TeacherMauPhieuCham/index')),
  TeacherXuLyPhieuTraLoi: lazy(
    () => import('./views/TeacherXuLyPhieuTraLoi/index')
  ),
  GradingRoundDetailView: lazy(
    () => import('./views/TeacherXuLyPhieuTraLoi/GradingRoundDetailView')
  ),

  Statistical: lazy(() => import('./views/Statistical/index')),
  PageTesting: lazy(() => import('./views/PageTesting/index'))
};

const LAYOUT_MAP = {
  main: MainLayout,
  minimal: MinimalLayout
};

const renderRoute = (routeDef, index) => {
  const Component = COMPONENT_REGISTRY[routeDef.component];
  const Layout = LAYOUT_MAP[routeDef.layout] || MainLayout;

  if (!Component) {
    console.warn(
      `[Routes] Component not found in registry: "${routeDef.component}" for path "${routeDef.path}"`
    );
    return null;
  }

  return (
    <RouteWithLayout
      key={`${routeDef.path}-${index}`}
      component={Component}
      layout={Layout}
      path={routeDef.path}
      exact={routeDef.exact !== false}
    />
  );
};

const Routes = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <ScrollToTop />
      <Switch>
        {PUBLIC_ROUTES.map(renderRoute)}
        {PROTECTED_ROUTES.map(renderRoute)}
        <Redirect to="/not-found" />
      </Switch>
    </Suspense>
  );
};

export default Routes;
